import { camelToKebab } from '@js/str-utils'

const DEFAULT_CSS_PROPERTIES = Object.freeze([
  'backgroundColor',
  'border',
  'borderRadius',
  'borderWidth',
  'boxShadow',
  'color',
  'cursor',
  'filter',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'textDecoration',
  'userSelect',
]);

const DEFAULT_CSS_VARIABLE_POSTFIXES = Object.freeze([
  '', 'hover', 'active', 'disabled',
]);

class CssStyleProcessor {
  constructor (
    cssVariablePrefix,
    cssVariableExtraPostfixes = [],
    cssVariablePostfixes = DEFAULT_CSS_VARIABLE_POSTFIXES,
    cssProperties = DEFAULT_CSS_PROPERTIES,
  ) {
    this.prefix = cssVariablePrefix,
    this.postfixes = [...cssVariablePostfixes, ...cssVariableExtraPostfixes];
    this.properties = cssProperties;
  }
  getExtendedStyle(styleObject, className) {
    return {
      ...this.getCssPropertyObject(styleObject),
      ...this.getCssVariableObject(styleObject, className),
    };
  }
  getCssPropertyObject (styleObject) {
    const keysToOmit = this.properties;
    if (isObject(styleObject)) {
      return Object.fromEntries(
        Object.entries(styleObject).filter(
          ([key, value]) => !keysToOmit.includes(key)
        )
      );
    }
    return {};
  }
  getCssVariableObject(styleObject, className) {
    if (!isObject(styleObject)) {
      return {};
    }

    const prefix = `${this.prefix}-${camelToKebab(className)}`;

    return this.properties.reduce((result, prop) => {
      const style = this.getObjectValue(styleObject, prop);
      const cssVariableName = `${prefix}-${camelToKebab(prop)}`;
      return Object.assign(
        result,
        this.postfixes
          .reduce((result, postfix) => {
            const key = cssVariableName + (postfix ? '-' : '') + postfix;
            if (style === null) {
              result[prop] = null;
            } else if (isString(style) || isNumber(style)) {
              result[prop] = style;
            } else {
              result[key] = style[postfix] ? style[postfix] : style[''];
            }
            return result;
          }, {}));
    }, {});
  }
  getObjectValue(object, key, defaultValue = null) {
    return Object.prototype.hasOwnProperty.call(object, key) ? object[key] : defaultValue;
  }
}

const DefaultPseudoClassAttributeNames = [
  'backgroundColor', 'border', 'borderRadius', 'borderWidth',
  'boxShadow', 'color', 'cursor', 'fontFamily', 'fontSize', 'fontWeight',
  'textDecoration', 'userSelect'
];

const DefaultPseudoClassVariableNames = [
  'background-color', 'border', 'border-radius', 'border-width',
  'box-shadow', 'color', 'cursor', 'font-family', 'font-size', 'font-weight',
  'text-decoration', 'user-select'
]

function isString (value) {
  return typeof value === 'string' || value instanceof String;
}

function isNumber (value) {
  return typeof value === 'number' && !isNaN(value);
}

function isObject (value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function getPropValue (val, key = '') {
  return val === null ? val : (isString(val) || isNumber(val)) ? val : val[key] ? val[key] : val[""];
}

function getPseudoClasses (val, varName, types = ['', 'hover'], separator = '-') {
  const pseudoClasses = types.reduce((result, type) => {
    const key = varName + (type ? separator + type : '');
    result[key] = getPropValue(val, type ? type : '');
    return result;
  }, {});
  return pseudoClasses
}

function getPseudoClassesObject (attrs, attrNames, classPrefix, classNames, classPostfixes, fillNull = false) {
  if (!attrs) { return {}; }

  return attrNames.reduce((result, elem, index) => {
    if (!fillNull && !attrs[elem]) { return result; }

    const attr = attrs[elem] ? attrs[elem] : null;
    const varName = classPrefix + classNames[index];
    const pseudoClasses = getPseudoClasses(attr, varName, classPostfixes);
    result = Object.assign(result, pseudoClasses);
    return result;
  }, {});
}

function filterObjectByArray(object, array) {
  return (!object || typeof object !== 'object') ? {} : Object.fromEntries(
    Object.entries(object).filter(([key, value]) => !array.includes(key)));
}

export {
  CssStyleProcessor,
  DefaultPseudoClassAttributeNames,
  DefaultPseudoClassVariableNames,
  getPropValue,
  getPseudoClasses,
  getPseudoClassesObject,
  filterObjectByArray,
  isString,
};
